// src/pages/PhoneNumber.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const selectClassName =
    "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

// --- COMPONENTES INTERNOS ---
// Modal Genérico
const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
            <Card className="relative w-full max-w-lg">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="absolute right-3 top-3"
                >
                    <XMarkIcon className="h-5 w-5" />
                </Button>
                <CardContent className="pt-6">{children}</CardContent>
            </Card>
        </div>
    );
};

// Formulário de PhoneNumber
const PhoneNumberForm = ({ phone, sips, assistants, onSave, onCancel, isSubmitting }) => {
    const [formData, setFormData] = useState({
        number: '',
        name: '',
        assistantId: '',
        credentialId: '',
        allowNonE164: false,
        ...phone
    });
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-2xl font-semibold">{phone?._id ? 'Editar Número' : 'Adicionar Novo Número'}</h2>
            <div>
                <Label htmlFor="number">Número de telefone</Label>
                <Input id="number" name="number" type="text" value={formData.number} onChange={handleChange} className="mt-1" required placeholder="5511999998888 ou +5511999998888" />
                <div className="mt-2 flex items-start gap-2">
                    <input
                        id="allowNonE164"
                        name="allowNonE164"
                        type="checkbox"
                        checked={!!formData.allowNonE164}
                        onChange={handleChange}
                        className="mt-1 h-4 w-4 rounded border-input"
                    />
                    <Label htmlFor="allowNonE164" className="font-normal leading-tight">
                        Permitir números sem <span className="font-mono">+</span> (não E.164)
                    </Label>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                    Ative esta opção para cadastrar formatos personalizados.
                </p>
            </div>
            <div>
                <Label htmlFor="name">Nome de Identificação</Label>
                <Input id="name" name="name" type="text" value={formData.name} onChange={handleChange} className="mt-1" required placeholder="Ex: Número Principal Vendas" />
            </div>
            <div>
                <Label htmlFor="credentialId">Credencial SIP Associada</Label>
                <select id="credentialId" name="credentialId" value={formData.credentialId} onChange={handleChange} className={`${selectClassName} mt-1`} required>
                    <option value="">Selecione um SIP</option>
                    {sips.map(sip => <option key={sip._id} value={sip.vapiId}>{sip.nome}</option>)}
                </select>
            </div>
            <div>
                <Label htmlFor="assistantId">Assistente de Voz Associado</Label>
                <select id="assistantId" name="assistantId" value={formData.assistantId} onChange={handleChange} className={`${selectClassName} mt-1`} required>
                    <option value="">Selecione um Assistente</option>
                    {assistants.map(a => <option key={a.vapiId} value={a.vapiId}>{a.nome || a.name}</option>)}
                </select>
            </div>
            <div className="pt-4 flex justify-end gap-4">
                <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancelar</Button>
                <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Salvando...' : 'Salvar'}</Button>
            </div>
        </form>
    );
};

// --- COMPONENTE PRINCIPAL ---
export default function PhoneNumbers() {
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [sips, setSips] = useState([]);
    const [assistants, setAssistants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingPhone, setEditingPhone] = useState(null);
    const [selectedPhoneDetails, setSelectedPhoneDetails] = useState(null); // Novo estado para detalhes
    const [clientes, setClientes] = useState([]);
    const [clienteFiltro, setClienteFiltro] = useState('');
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const clienteId = userData?.clienteId;
    const isAdmin = userData?.role === 'admin';
    const tokenClient = localStorage.getItem('tokenClient');
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
    // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';

    const getAuthConfig = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("Sessão expirada. Faça login novamente.");
            // Opcional: redirecionar para o login
            return null;
        }
        return {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };
    };
    const normalizeId = (value) => {
        if (!value) return '';
        if (typeof value === 'object') {
            if (value._id) return String(value._id);
            if (value.id) return String(value.id);
        }
        return String(value);
    };
    const clientesMap = useMemo(() => {
        return new Map(clientes.map(c => [String(c._id), c]));
    }, [clientes]);

    const fetchData = useCallback(async () => {
        if (!clienteId && !isAdmin) {
            setError('ID do cliente não encontrado. Faça login novamente.');
            setLoading(false);
            return;
        }
        setLoading(true);
        const toastId = toast.loading('Carregando dados...');
        try {
            const config = getAuthConfig();
            if (!config) return setLoading(false); // Interrompe se não houver token
            const activeClienteId = isAdmin ? clienteFiltro : clienteId;
            const params = { tokenClient };
            if (activeClienteId) {
                params.clienteId = activeClienteId;
            }
            const [phoneRes, sipsRes, assistantsRes] = await Promise.all([
                axios.get(`${baseUrl}/api/phoneNumbers`, { params, ...config }),
                axios.get(`${baseUrl}/api/sips`, { params, ...config }),
                axios.get(`${baseUrl}/api/assistentes/master`, { params, ...config })
            ]);
            setPhoneNumbers(phoneRes.data);
            setSips(sipsRes.data);
            setAssistants(assistantsRes.data);
            setError('');
            toast.dismiss(toastId);
        } catch (err) {
            setError('Falha ao carregar dados. Tente atualizar a página.');
            toast.error('Não foi possível carregar os dados.', { id: toastId });
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [clienteId, tokenClient, isAdmin, clienteFiltro]);

    const fetchClientes = useCallback(async () => {
        if (!isAdmin) return;
        const config = getAuthConfig();
        if (!config) return;
        try {
            const res = await axios.get(`${baseUrl}/api/usuarios`, config);
            setClientes(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Erro ao buscar clientes:', err.response?.data || err.message);
        }
    }, [isAdmin, baseUrl]);

    // Criar mapas completos para SIPs e Assistentes
    const sipsCompleteMap = useMemo(() => {
        return new Map(sips.map(sip => [sip._id, sip]));
    }, [sips]);

    const assistantsCompleteMap = useMemo(() => {
        return new Map(assistants.map(assistant => [assistant.vapiId, assistant]));
    }, [assistants]);

    // Função para pegar SIP e Assistente de um PhoneNumber
    const getSipAndAssistant = (phoneNumber) => {
        const sip = phoneNumber.credentialId ? sipsCompleteMap.get(phoneNumber.credentialId?._id) : null;
        const assistant = phoneNumber.assistantId ? assistantsCompleteMap.get(phoneNumber.assistantId) : null;
        return { sip, assistant };
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);
    useEffect(() => {
        fetchClientes();
    }, [fetchClientes]);

    const handleSavePhone = async (formData) => {
        setIsSubmitting(true);
        const toastId = toast.loading(formData._id ? 'Atualizando número...' : 'Adicionando número...');
        const isEditing = !!formData._id;
        const url = isEditing ? `${baseUrl}/api/phoneNumbers/${formData._id}` : `${baseUrl}/api/phoneNumbers`;
        const method = isEditing ? 'put' : 'post';
        const payload = {
            ...formData,
            clienteId,
            provider: 'byo-phone-number', // Provider fixo como na lógica original
            tokenClient
        };
        try {
            const config = getAuthConfig();
            if (!config) return setIsSubmitting(false); // Interrompe se não houver token
            await axios[method](url, payload, config);
            toast.success(isEditing ? 'Número atualizado!' : 'Número adicionado!', { id: toastId });
            setIsModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Ocorreu um erro.', { id: toastId });
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeletePhone = async (phone) => {
        if (window.confirm(`Tem certeza que deseja excluir o número "${phone.name}" (${phone.number})?`)) {
            const toastId = toast.loading('Excluindo número...');
            try {
                const config = getAuthConfig();
                if (!config) return; // Interrompe se não houver token
                await axios.delete(`${baseUrl}/api/phoneNumbers/${phone._id}`, {
                    data: { tokenClient },  // Passa o tokenClient no corpo da requisição
                    ...config
                });
                toast.success('Número excluído!', { id: toastId });
                fetchData();
            } catch (err) {
                toast.error('Erro ao excluir.', { id: toastId });
                console.error(err);
            }
        }
    };

    // Adicionar um mapa para nomes de SIP (para manter a tabela consistente)
    const sipsMap = useMemo(() => new Map(sips.map(s => [s._id, s.nome])), [sips]);
    const assistantsMap = useMemo(() => new Map(assistants.map(a => [a.vapiId, a.nome || a.name])), [assistants]);

    const phoneNumbersFiltrados = useMemo(() => {
        if (!isAdmin || !clienteFiltro) return phoneNumbers;
        return phoneNumbers.filter(phone => normalizeId(phone.clienteId) === clienteFiltro);
    }, [phoneNumbers, isAdmin, clienteFiltro]);

    return (
        <>
            {/* Modal para edição/criação */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <PhoneNumberForm
                    phone={editingPhone}
                    sips={sips}
                    assistants={assistants}
                    onSave={handleSavePhone}
                    onCancel={() => setIsModalOpen(false)}
                    isSubmitting={isSubmitting}
                />
            </Modal>
            {/* Modal para exibir detalhes */}
            <Modal isOpen={!!selectedPhoneDetails} onClose={() => setSelectedPhoneDetails(null)}>
                {selectedPhoneDetails && (
                    <div>
                        <h2 className="text-2xl font-semibold mb-6">Detalhes do Número</h2>
                        <div className="mb-4">
                            <h3 className="font-semibold text-lg mb-2">Informações do Número</h3>
                            <p><span className="font-medium">ID:</span> {selectedPhoneDetails.phone.vapiId}</p>
                            <p><span className="font-medium">Nome:</span> {selectedPhoneDetails.phone.name}</p>
                            <p><span className="font-medium">Número:</span> {selectedPhoneDetails.phone.number}</p>
                            <p><span className="font-medium">Criado em:</span> {new Date(selectedPhoneDetails.phone.criadoEm).toLocaleString()}</p>
                            <p><span className="font-medium">Atualizado em:</span> {new Date(selectedPhoneDetails.phone.atualizadoEm).toLocaleString()}</p>
                        </div>
                        <div className="mb-4">
                            <h3 className="font-semibold text-lg mb-2">Informações do SIP</h3>
                            {selectedPhoneDetails.sip ? (
                                <>
                                    <p><span className="font-medium">Nome:</span> {selectedPhoneDetails.sip.nome}</p>
                                    <p><span className="font-medium">Usuário:</span> {selectedPhoneDetails.sip.usuario}</p>
                                    {selectedPhoneDetails.sip.sipInfo && (
                                        <>
                                            <p><span className="font-medium">Nome SIP:</span> {selectedPhoneDetails.sip.sipInfo.nome}</p>
                                            <p><span className="font-medium">Usuário SIP:</span> {selectedPhoneDetails.sip.sipInfo.usuario}</p>
                                        </>
                                    )}
                                </>
                            ) : (
                                <p>Nenhum SIP associado encontrado.</p>
                            )}
                        </div>
                        <div className="mb-4">
                            <h3 className="font-semibold text-lg mb-2">Informações do Assistente</h3>
                            {selectedPhoneDetails.assistant ? (
                                <>
                                    <p><span className="font-medium">Nome:</span> {selectedPhoneDetails.assistant.nome || selectedPhoneDetails.assistant.name}</p>
                                    <p><span className="font-medium">ID:</span> {selectedPhoneDetails.assistant.vapiId}</p>
                                    {selectedPhoneDetails.assistant._id && (
                                        <p><span className="font-medium">ID do Assistente:</span> {selectedPhoneDetails.assistant._id}</p>
                                    )}
                                    {/* Adicione outros campos conforme necessário */}
                                </>
                            ) : (
                                <p>Nenhum assistente associado encontrado.</p>
                            )}
                        </div>
                        <Button onClick={() => setSelectedPhoneDetails(null)} className="mt-4">
                            Fechar
                        </Button>
                    </div>
                )}
            </Modal>
            <div className="space-y-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight">Gerenciamento de Números</h1>
                        <p className="text-sm text-muted-foreground">Cadastre e administre seus números SIP.</p>
                    </div>
                    <Button onClick={() => { setEditingPhone(null); setIsModalOpen(true); }}>
                        <PlusCircleIcon className="h-5 w-5" />
                        Adicionar Número
                    </Button>
                </div>
                {isAdmin && (
                    <div className="max-w-md">
                        <Label className="text-xs text-muted-foreground">Cliente</Label>
                        <select
                            value={clienteFiltro}
                            onChange={(e) => setClienteFiltro(e.target.value)}
                            className={`${selectClassName} mt-1`}
                        >
                            <option value="">Todos os clientes</option>
                            {clientes.map(cliente => (
                                <option key={cliente._id} value={String(cliente._id)}>
                                    {cliente.nome || cliente.name || cliente.email || String(cliente._id)}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <Card className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-muted/50 text-muted-foreground">
                            <tr>
                                <th className="p-4 font-semibold">Nome</th>
                                <th className="p-4 font-semibold">Número</th>
                                <th className="p-4 font-semibold">SIP Associado</th>
                                <th className="p-4 font-semibold">Assistente Associado</th>
                                {isAdmin && <th className="p-4 font-semibold">Cliente</th>}
                                <th className="p-4 font-semibold text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center p-8 text-muted-foreground">Carregando números...</td></tr>
                            ) : error ? (
                                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center p-8 text-destructive"><ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{error}</td></tr>
                            ) : phoneNumbersFiltrados.length === 0 ? (
                                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center p-8 text-muted-foreground">Nenhum número de telefone cadastrado.</td></tr>
                            ) : (
                                phoneNumbersFiltrados.map((phone) => {
                                    const { sip, assistant } = getSipAndAssistant(phone);
                                    return (
                                        <tr key={phone._id} className="border-b border-border transition-colors hover:bg-muted/40">
                                            <td className="p-4 font-medium">{phone.name}</td>
                                            <td className="p-4 font-mono text-sm">{phone.number}</td>
                                            <td className="p-4">{sip ? sip.nome : <Badge variant="secondary">Não encontrado</Badge>}</td>
                                            <td className="p-4">{assistant ? (assistant.nome || assistant.name) : <Badge variant="secondary">Não encontrado</Badge>}</td>
                                            {isAdmin && (
                                                <td className="p-4">
                                                    {(() => {
                                                        const clienteIdValue = normalizeId(phone.clienteId);
                                                        const cliente = clientesMap.get(clienteIdValue);
                                                        return cliente?.nome || cliente?.name || cliente?.email || clienteIdValue || 'N/A';
                                                    })()}
                                                </td>
                                            )}
                                            <td className="p-4 text-center flex justify-center items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        const { sip, assistant } = getSipAndAssistant(phone);
                                                        setSelectedPhoneDetails({
                                                            phone,
                                                            sip,
                                                            assistant
                                                        });
                                                    }}
                                                    title="Ver detalhes"
                                                >
                                                    <InformationCircleIcon className="h-5 w-5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => { setEditingPhone(phone); setIsModalOpen(true); }}
                                                    title="Editar"
                                                >
                                                    <PencilSquareIcon className="h-5 w-5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDeletePhone(phone)}
                                                    title="Excluir"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </Card>
            </div>
        </>
    );
}
